import { mainBackendApi } from "@/services";
import {
	ChangePasswordArgs,
	ForgotPasswordArgs,
	GoogleAuthArgs,
	LoginArgs,
	RegisterArgs,
	ResetPasswordArgs,
	Token,
	VerifyEmailArgs,
} from "./types";

export const AuthService = mainBackendApi.injectEndpoints({
	endpoints: (build) => ({
		login: build.query<Token, LoginArgs>({
			query: (arg) => ({
				url: "/auth/login",
				method: "POST",
				body: {
					email: arg.login,
					password: arg.password,
				},
			}),
			transformResponse: (raw: any): Token => ({
				accessToken: raw.accessToken ?? raw.access_token,
				refreshToken: raw.refreshToken ?? raw.refresh_token,
				expiresIn: raw.expiresIn ?? raw.expires_in,
				tokenType: raw.tokenType ?? raw.token_type ?? "bearer",
			}),
		}),
		logout: build.query<void, void>({
			query: () => ({
				url: "/auth/logout",
				method: "POST",
			}),
		}),
		refresh: build.query<Token, string>({
			query: (refreshToken) => ({
				url: "/auth/refresh-token",
				method: "POST",
				body: { refresh_token: refreshToken },
			}),
			transformResponse: (raw: any): Token => ({
				accessToken: raw.accessToken ?? raw.access_token,
				refreshToken: raw.refreshToken ?? raw.refresh_token,
				expiresIn: raw.expiresIn ?? raw.expires_in,
				tokenType: raw.tokenType ?? raw.token_type ?? "bearer",
			}),
		}),
		register: build.query<Token, RegisterArgs>({
			query: (arg) => ({
				url: "/auth/register",
				method: "POST",
				body: {
					email: arg.email,
					password: arg.password,
					organization_id: arg.organizationId,
					first_name: arg.firstName,
					last_name: arg.lastName,
					...(arg.referralCode
						? { referral_code: arg.referralCode }
						: {}),
				},
			}),
			transformResponse: (raw: any): Token => ({
				accessToken: raw.accessToken ?? raw.access_token,
				refreshToken: raw.refreshToken ?? raw.refresh_token,
				expiresIn: raw.expiresIn ?? raw.expires_in,
				tokenType: raw.tokenType ?? raw.token_type ?? "bearer",
			}),
		}),
		verifyEmail: build.query<string, VerifyEmailArgs>({
			query: (arg) => ({
				url: "/auth/verify-email",
				method: "POST",
				body: { code: arg.code },
			}),
		}),
		resendVerification: build.query<string, void>({
			query: () => ({
				url: "/auth/resend-verification",
				method: "POST",
			}),
		}),
		googleAuth: build.query<Token, GoogleAuthArgs>({
			query: (arg) => ({
				url: "/auth/google",
				method: "POST",
				body: {
					id_token: arg.idToken,
					organization_id: arg.organizationId,
					...(arg.referralCode
						? { referral_code: arg.referralCode }
						: {}),
				},
			}),
			transformResponse: (raw: any): Token => ({
				accessToken: raw.accessToken ?? raw.access_token,
				refreshToken: raw.refreshToken ?? raw.refresh_token,
				expiresIn: raw.expiresIn ?? raw.expires_in,
				tokenType: raw.tokenType ?? raw.token_type ?? "bearer",
			}),
		}),
		changePassword: build.mutation<void, ChangePasswordArgs>({
			query: (arg) => ({
				url: "/auth/change-password",
				method: "POST",
				body: {
					current_password: arg.currentPassword,
					new_password: arg.newPassword,
				},
			}),
		}),
		forgotPassword: build.query<void, ForgotPasswordArgs>({
			query: (arg) => ({
				url: "/auth/forgot-password",
				method: "POST",
				body: { email: arg.email },
			}),
		}),
		resetPassword: build.query<void, ResetPasswordArgs>({
			query: (arg) => ({
				url: "/auth/reset-password",
				method: "POST",
				body: {
					email: arg.email,
					code: arg.code,
					new_password: arg.newPassword,
				},
			}),
		}),
	}),
});
